import styled, { keyframes, css } from "styled-components";

const spinRing = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmerText = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

export const ThinkingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ProcessingRing = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #001d30 0%, #001d30 70%, #67D9D4 85%, transparent 100%);
  mask: radial-gradient(circle, transparent 54%, black 55%);
  -webkit-mask: radial-gradient(circle, transparent 54%, black 55%);
  animation: ${spinRing} 0.85s linear infinite;
  flex-shrink: 0;
`;

export const ProcessingShimmerText = styled.span`
  font-size: 0.8rem;
  font-style: italic;
  font-weight: 500;
  background: linear-gradient(
    90deg,
    #001d30 0%,
    #001d30 35%,
    #67D9D4 55%,
    #001d30 70%,
    #001d30 100%
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmerText} 2.5s linear infinite;
`;

export { ProcessingRing as ThinkingRing, ProcessingShimmerText as ThinkingText };

/* ---- Live checklist (replaces the single rotating line) ---- */

export const ChecklistWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const ChecklistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ChecklistTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.primary};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChecklistCount = styled.div`
  font-size: 0.72rem;
  font-family: ui-monospace, "SF Mono", monospace;
  color: ${(props) => props.theme.colors.font.tertiary};
  white-space: nowrap;

  b {
    color: ${(props) => props.theme.colors.accentColor};
    font-weight: 700;
  }
`;

export const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${(props) => props.theme.colors.border};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: ${(props) => props.theme.colors.accentColor};
  border-radius: 2px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 168px;
  overflow-y: auto;
`;

export const TaskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 2px;
`;

export const TaskIconWrapper = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const TaskDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.font.grey2};
`;

const drawCheck = keyframes`
  to { stroke-dashoffset: 0; }
`;

export const CheckCircle = styled.circle`
  fill: ${(props) => props.theme.colors.sidebar.selected};
  stroke: ${(props) => props.theme.colors.accent2};
`;

export const CheckMark = styled.path`
  stroke: ${(props) => props.theme.background.primary};
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  animation: ${drawCheck} 0.4s ease forwards;
`;

// Same gradient/animation as ProcessingShimmerText, applied per row instead of
// to a single rotating line. Each row's sweep starts at a different offset —
// synchronized, all seven would pulse together like a neon sign; staggered,
// it reads as a calm wave.
export const TaskLabel = styled.span<{ $done: boolean; $index: number }>`
  font-size: 0.83rem;

  ${(props) =>
    props.$done
      ? css`
          color: ${props.theme.colors.font.grey2};
        `
      : css`
          font-weight: 500;
          background: linear-gradient(
            90deg,
            #001d30 0%,
            #001d30 35%,
            #67D9D4 55%,
            #001d30 70%,
            #001d30 100%
          );
          background-size: 200% auto;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: ${shimmerText} 2.5s linear infinite;
          animation-delay: ${-0.4 * props.$index}s;
        `}
`;
